import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";

import App from "./App.jsx";

// Admin scraper fetch/console interception removed - scraper disabled

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 15 * 60 * 1000, // keep cache for 15 minutes
      refetchOnWindowFocus: false,
    },
  },
});

if (typeof window !== "undefined" && window.sessionStorage) {
  const persister = createSyncStoragePersister({
    storage: window.sessionStorage,
  });

  // Skip persistence for large / admin-only payloads. The inventory-all
  // response (limit=200) was the bulk of the persisted sessionStorage blob
  // and is cheap to refetch on reload. Same for admin queries.
  const SKIP_PERSIST_KEYS = new Set(["inventory-all", "admin"]);

  persistQueryClient({
    queryClient,
    persister,
    maxAge: 10 * 60 * 1000, // cap persistence at 10 minutes
    dehydrateOptions: {
      shouldDehydrateQuery: (query) => {
        const head = query.queryKey?.[0];
        return !SKIP_PERSIST_KEYS.has(head);
      },
    },
  });
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);

// Register the image-caching Service Worker. It intercepts <img> requests
// and serves them from a persistent Cache API store, so repeat visits skip
// the network entirely. Skipped in dev — Vite's HMR and a stale SW cache
// fight each other and the SW just adds debugging friction locally.
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw-images.js").catch(() => {
      // Registration failures are non-fatal — the site works fine without
      // the SW, the user just doesn't get the persistent image cache.
    });
  });
}
